import React from "react";
import { Link } from "react-router-dom";
import { LogIn, KeyRound, Upload, BarChart3, ArrowRight } from "lucide-react";

/* ── Methodology flow ────────────────────────────────────────────────────── */
const FLOW_STEPS = [
  {
    code: "GST Purchase Data",
    desc: "Vendor-wise spend records from GSTR-2A / 2B",
    detail: "Financial spend",
  },
  {
    code: "HSN Code Mapping",
    desc: "Each line item mapped to a product/sector category",
    detail: "Sectoral classification",
  },
  {
    code: "Emission Factors",
    desc: "EXIOBASE MRIO + India-specific datasets applied",
    detail: "kg CO₂e / ₹1,000",
  },
  {
    code: "Carbon Intensity",
    desc: "Aggregated carbon intensity score per organisation",
    detail: "Composite metric",
  },
  {
    code: "Green Score & Tier",
    desc: "0–100 index + risk classification assigned",
    detail: "Output",
  },
];

/* ── Platform steps ──────────────────────────────────────────────────────── */
const PLATFORM_STEPS = [
  {
    icon: <LogIn size={20} />,
    step: "01",
    title: "OTP Login",
    desc: "Authenticate securely using your registered mobile number. No password required.",
  },
  {
    icon: <KeyRound size={20} />,
    step: "02",
    title: "Activate Access",
    desc: "Use your free trial (2 runs, no card needed) or an access code issued by your bank or partner.",
  },
  {
    icon: <Upload size={20} />,
    step: "03",
    title: "Upload GSTR Data",
    desc: "Upload your GSTR-1 (Sales) or GSTR-2B (Procurement) export. Standard GST portal column names accepted — CSV, Excel, or JSON.",
  },
  {
    icon: <BarChart3 size={20} />,
    step: "04",
    title: "Receive Score & Classification",
    desc: "Instantly receive your Carbon Intensity, Green Score, and Risk Tier classification.",
  },
];

export default function HowItWorks() {
  return (
    <section className="howitworks-section" id="how-it-works">
      <div className="landing-container">

        {/* ── Methodology flow ── */}
        <div className="section-label">METHODOLOGY</div>
        <h2 className="section-title">From Spend to Carbon Risk Insight</h2>
        <p className="section-sub">
          A transparent, reproducible pipeline that converts GST purchase data into
          a structured carbon risk assessment — no primary emissions data required.
        </p>

        <div className="flow-diagram">
          {FLOW_STEPS.map((step, i) => (
            <React.Fragment key={i}>
              <div className="flow-node">
                <div className="flow-node-detail">{step.detail}</div>
                <div className="flow-node-code">{step.code}</div>
                <div className="flow-node-desc">{step.desc}</div>
              </div>
              {i < FLOW_STEPS.length - 1 && (
                <div className="flow-connector" aria-hidden="true">
                  <div className="flow-connector-line" />
                  <ArrowRight size={14} className="flow-connector-arrow" />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* ── Solution callouts ── */}
        <div className="solution-strip">
          <div className="solution-item">
            <span className="solution-check">✓</span>
            Uses existing GST data — no new data collection
          </div>
          <div className="solution-item">
            <span className="solution-check">✓</span>
            Scalable to thousands of entities simultaneously
          </div>
          <div className="solution-item">
            <span className="solution-check">✓</span>
            Results in minutes, not months
          </div>
        </div>

        {/* ── Platform steps ── */}
        <div className="section-label" style={{ marginTop: "5rem" }}>HOW IT WORKS</div>
        <h2 className="section-title" id="platform-steps">Using the Platform</h2>

        <div className="platform-steps-grid">
          {PLATFORM_STEPS.map((s, i) => (
            <div key={i} className="platform-step-card">
              <div className="platform-step-header">
                <div className="platform-step-icon">{s.icon}</div>
                <div className="platform-step-number">{s.step}</div>
              </div>
              <h3 className="platform-step-title">{s.title}</h3>
              <p className="platform-step-desc">{s.desc}</p>
            </div>
          ))}
        </div>

        <div className="howitworks-cta">
          <Link to="/login" className="hero-cta-primary">
            Begin Screening
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}
